import type { SessionCapabilities, SessionProtocol, SessionTokenPair } from './session.js';

/** Only a positively identified old-server route miss permits legacy login. */
export async function negotiateSessionProtocol(read: () => Promise<SessionCapabilities>): Promise<SessionProtocol | undefined> {
  let capabilities: SessionCapabilities;
  try { capabilities = await read(); } catch (error) {
    if ((error as { status?: number } | undefined)?.status === 404) return undefined;
    if ((error as { code?: string } | undefined)?.code === 'service_unconfigured') throw Object.assign(new Error('service_unconfigured'), { code: 'service_unconfigured' });
    throw new Error('session_capabilities_unavailable');
  }
  if (capabilities.supportedProtocols?.includes('rotating_v1')) return 'rotating_v1';
  throw new Error('session_protocol_unavailable');
}

export function validateSessionPair(pair: SessionTokenPair, expected: SessionProtocol | undefined): void {
  if (pair.sessionProtocol !== expected || !pair.accessToken || !pair.refreshToken || !Number.isFinite(Date.parse(pair.expiresAt)) ||
      (expected && (!/^vrt1_[A-Za-z0-9_-]{43}$/.test(pair.refreshToken) || !pair.refreshExpiresAt || !Number.isFinite(Date.parse(pair.refreshExpiresAt))))) {
    throw new Error('session_invalid');
  }
}

/** A bounded result, with no credential-bearing retry queue or error payload. */
export async function boundedSessionRevoke(run: () => Promise<unknown>, timeoutMs = 5000): Promise<'confirmed' | 'unconfirmed'> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve().then(run).then(() => 'confirmed' as const, () => 'unconfirmed' as const),
      new Promise<'unconfirmed'>(resolve => { timer = setTimeout(() => resolve('unconfirmed'), timeoutMs); }),
    ]);
  } finally { clearTimeout(timer); }
}
